import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { colDefActiveDuration } from '@customer/components/tables/grid/columnDefs';
import GeofenceInteractionTypeRenderer from '@customer/components/tables/cells/geofence-interaction-type/GeofenceInteractionTypeRenderer';
import {
  AssetGIFilters,
  AssetListScenarios,
  useGetAssetInteractionsSummaryQuery,
  useListAssetGeofenceInteractionsQuery,
} from '@customer/store/apis/assetsApi';
import { GIListModel } from '@customer/store/apis/interactionsApi';
import { useAppSelector, useAppDispatch } from '@customer/store/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import FilterButton from '@customer/components/inputs/filter-button/FilterButton';
import FilterSummary from '@customer/components/inputs/filter-summary/FilterSummary';
import SubTabsPageContent from '@customer/templates/sub-tabs-page/SubTabsPageContent';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import { AssetGIScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import HistogramChartScenario from '@customer/components/charts/histogram-chart-scenario/HistogramChartScenario';
import BarChartByGeofenceInteractionType from '@customer/components/charts/by-geofence-interaction/BarChartByGeofenceInteractionType';
import AreaChartScenario from '@customer/components/charts/area-chart-scenario/AreaChartScenario';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import {
  GeofenceInteractionSidePanelTab,
  openSidePanel,
  SidePanel,
  usePerformanceTimeRangeFilters,
} from '@customer/store/slices/globalSlice';
import InsightsCardGI from '@customer/components/data-display/insights-card-gi/InsightsCardGI';
import useLocationFilterController from '@customer/components/maps/location-filter/useLocationFilterController';
import { useGetGIHeatmapQuery } from '@customer/store/apis/heatmapsApi';
import LocationFilter from '@customer/components/maps/location-filter/LocationFilter';
import { useLocationFilterParams } from '@customer/store/slices/locationFilterSlice';
import PerformanceTimeRange from '@customer/components/inputs/performance-time-range/PerformanceTimeRange';
import {
  useDistanceFormatter,
  useSpeedFormatter,
} from '@customer/components/tables/formatters/numberFormatters';
import {
  GoodInvalidFormatter,
  YesNoFormatter,
} from '@customer/components/tables/formatters/booleanFormatters';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';
import { useGeofenceNameValueGetter } from '@customer/components/tables/cells/geofence-name/useGeofenceNameValueGetter';

const gridSliceKey = 'AssetGeofenceInteractions';
const filterGroup = FilterGroup.AssetGI;
type Model = GIListModel;

export function SingleAssetGeofenceInteractions() {
  const { assetId } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const siteId = useAppSelector(state => state.auth.siteId);

  const timeRangeFilters = usePerformanceTimeRangeFilters();

  const filters = useFilters<AssetGIFilters>(filterGroup);

  const locationFilters = useLocationFilterParams({
    filterGroup,
    includeAssetId: true,
    assetId,
  });

  const speedFormatter = useSpeedFormatter();
  const distanceFormatter = useDistanceFormatter();
  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();
  const geofenceNameValueGetter = useGeofenceNameValueGetter<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(
    () => [
      {
        field: 'ts',
        headerName: t('Date and Time'),
        sort: 'desc',
        minWidth: 180,
        ...absoluteTimeColDef,
      },
      {
        field: 'it',
        headerName: t('InteractionType'),
        cellRenderer: GeofenceInteractionTypeRenderer,
        minWidth: 180,
      },
      {
        field: 'geo',
        headerName: t('Geofence'),
        valueGetter: geofenceNameValueGetter,
      },
      { ...colDefActiveDuration(t('Duration')) },
      {
        field: 'dist',
        headerName: t('Distance'),
        valueFormatter: distanceFormatter<Model, 'dist'>,
      },
      {
        field: 'loSpd',
        headerName: t('Speed'),
        headerTooltip: t('Speed at event start'),
        valueFormatter: speedFormatter<Model, 'loSpd'>,
      },
      {
        field: 'aud',
        headerName: t('Audible'),
        valueFormatter: YesNoFormatter<Model, 'aud'>,
        cellRenderer: null,
      },
      {
        field: 'svfix',
        headerName: t('GpsFix'),
        valueFormatter: GoodInvalidFormatter<Model, 'svfix'>,
        cellRenderer: null,
      },
    ],
    [t, speedFormatter, distanceFormatter, absoluteTimeColDef, geofenceNameValueGetter],
  );

  const isSkipQuery = typeof assetId === 'undefined' || typeof siteId === 'undefined';

  const { data: interactionsData, isFetching } = useListAssetGeofenceInteractionsQuery(
    isSkipQuery
      ? skipToken
      : {
          assetId,
          query: {
            ...filters,
            ...locationFilters,
            ...timeRangeFilters,
            scenario: AssetListScenarios.PerformanceGeofenceInteractions,
          },
        },
    { refetchOnMountOrArgChange: 10 },
  );

  const { currentData: interactionsSummaryData } = useGetAssetInteractionsSummaryQuery(
    isSkipQuery
      ? skipToken
      : {
          assetId,
          query: {
            ...filters,
            ...locationFilters,
            ...timeRangeFilters,
            scenario: AssetListScenarios.PerformanceGeofenceInteractions,
          },
        },
    { refetchOnMountOrArgChange: 60 },
  );

  const onRowClick = useCallback(
    (data: Model) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.GeofenceInteraction,
          tab: GeofenceInteractionSidePanelTab.Summary,
          modelId: data.id.toString(),
        }),
      );
    },
    [dispatch],
  );

  const getDataSetLabel = useCallback(
    (label: string) => {
      return t('gi.counts');
    },
    [t],
  );

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByTimeRange'),
        ChartComponent: AreaChartScenario<AssetGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByTimeRange,
        hideResultsLimited: true,
        enableCustomTimeRange: true,
      },
      {
        title: t('ByTimeOfDay'),
        ChartComponent: HistogramChartScenario<AssetGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByTimeOfDay,
        hideResultsLimited: true,
      },
      {
        title: t('ByInteractionType'),
        ChartComponent: BarChartByGeofenceInteractionType<AssetGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByInteractionType,
        hideResultsLimited: true,
      },
    ],
    [t],
  );

  const locationFilterProps = useLocationFilterController({
    filterGroup,
    useHeatmapQuery: useGetGIHeatmapQuery,
    includeAssetId: true,
    assetId,
  });

  return (
    <SubTabsPageContent
      headerSummary={
        <FilterSummary
          transKey="ShowingInteractions"
          filteredTransKey="ShowingFilteredInteractions"
          countAll={interactionsData?.countAll}
          countFilter={interactionsData?.countFilter}
          filterGroup={filterGroup}
        />
      }
      headerActions={
        <>
          <PerformanceTimeRange disabled={isFetching} />
          <FilterButton filters={filterGroup} disabled={isFetching} />
        </>
      }
      body={
        <>
          <InsightsCardGI records={interactionsSummaryData?.gi} />

          <ChartScenarios
            title={t('GeofenceInteractions')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={filterGroup}
            colorScheme={ChartColorScheme.Blue}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="InteractionsWithCount"
            includeAssetId
            assetId={assetId}
            includeTimeRange
          />

          <LocationFilter {...locationFilterProps} />

          <AnalyticsTable
            title={t('GeofenceInteractions')}
            hasData={typeof interactionsData !== 'undefined'}
            noDataDescription={t('NoGeofenceInteractions')}
          >
            <GridControlled<Model>
              sliceKey={gridSliceKey}
              rowData={interactionsData?.items ?? []}
              columnDefs={columnDefs}
              onRowClick={onRowClick}
              noRowsMessage={t('NoGeofenceInteractions')}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default SingleAssetGeofenceInteractions;
